import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  Link,
  IconButton,
  Slider,
  CircularProgress,
  Alert,
  Button,
  Checkbox,
  keyframes,
  alpha,
} from "@mui/material";
import {
  PlayArrow,
  Pause,
  SkipNext,
  SkipPrevious,
  Refresh,
  Loop,
  Shuffle,
  PlayCircleOutline,
  LoopOutlined,
  ShuffleOutlined,
  MusicNote,
} from "@mui/icons-material";
import axiosInstance from "../api/axiosConfig";
import VideoList from "./VideoList";
import DraggableVideoList from "./DraggableVideoList";
import { describePlayback, useAudioPlayer } from "../hooks/useAudioPlayer";

// --- Helper
const formatTime = (seconds: number): string => {
  if (!seconds || isNaN(seconds)) return "0:00";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const fadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`;

const marquee = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

interface AudioDetails {
  title: string;
  artist?: string;
}

interface RandomAudio {
  vid: string;
  title: string;
  artist?: string;
}

// --- PlaybackControls sub-component (reusable for both full and compact modes)
interface ControlsProps {
  audioDetails: AudioDetails | null;
  currentTime: number;
  duration: number;
  bufferedTime: number;
  isLoading: boolean;
  isBuffering: boolean;
  isPlaying: boolean;
  displayError: string | null;
  /** short honest status ("Buffering — 12s of audio ready", ...) or null */
  statusText: string | null;
  /** offer a manual retry inside the status line */
  statusCanRetry: boolean;
  autoPlay: boolean;
  loopPlaylist: boolean;
  shufflePlaylist: boolean;
  nextTracks: RandomAudio[];
  isPlaylistMode: boolean;
  playlist: any[];
  playlistIndex: number;
  onTogglePlay: () => void;
  onSeek: (t: number) => void;
  onFetchData: () => void;
  onRetryAudio: () => void;
  fetchError: string | null;
  setAutoPlay: (v: boolean) => void;
  setLoopPlaylist: (v: boolean) => void;
  setShufflePlaylist: (v: boolean) => void;
  setPlaylistIndex: (fn: (i: number) => number) => void;
  onNavigateBack: () => void;
  onNavigateNext: (vid: string) => void;
  mode: "full" | "compact";
}

const PlaybackControls: React.FC<ControlsProps> = ({
  audioDetails,
  currentTime,
  duration,
  bufferedTime,
  isLoading,
  isBuffering,
  isPlaying,
  displayError,
  statusText,
  statusCanRetry,
  autoPlay,
  loopPlaylist,
  shufflePlaylist,
  nextTracks,
  isPlaylistMode,
  playlist,
  playlistIndex,
  onTogglePlay,
  onSeek,
  onFetchData,
  onRetryAudio,
  fetchError,
  setAutoPlay,
  setLoopPlaylist,
  setShufflePlaylist,
  setPlaylistIndex,
  onNavigateBack,
  onNavigateNext,
  mode,
}) => {
  const navigate = useNavigate();

  // Local scrub value: dragging must not fire a seek (and therefore a range
  // request) on every pixel — that is exactly what kills a weak connection.
  const [scrubTime, setScrubTime] = useState<number | null>(null);

  const handlePlayPause = () => onTogglePlay();

  const handleNext = () => {
    if (isPlaylistMode && playlist && playlist.length > 0) {
      // the new source starts on its own (see useAudioPlayer autoPlay)
      if (shufflePlaylist) {
        if (playlist.length === 1) return;
        let r = Math.floor(Math.random() * playlist.length);
        if (r === playlistIndex) r = (r + 1) % playlist.length;
        setPlaylistIndex(() => r);
      } else if (loopPlaylist) {
        setPlaylistIndex((i) => (i + 1) % playlist.length);
      } else {
        setPlaylistIndex((i) => Math.min(i + 1, playlist.length - 1));
      }
    } else {
      if (nextTracks.length > 0) navigate(`/music/${nextTracks[0].vid}`);
    }
  };

  if (mode === "compact") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1, sm: 2 },
          width: "100%",
          px: { xs: 1.5, sm: 3 },
          py: 1,
        }}
      >
        {/* Track info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, flex: "0 1 auto" }}>
          <MusicNote sx={{ color: "rgba(255,255,255,0.5)", fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ overflow: "hidden", width: { xs: 140, sm: 300 }, position: "relative" }}>
              <Box
                sx={{
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  animation: `${marquee} 10s linear infinite`,
                  "&:hover": { animationPlayState: "paused" },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    fontWeight: "medium",
                  }}
                >
                  {audioDetails?.title || (isLoading ? "Loading..." : "Unknown Title")} &nbsp;&nbsp; {audioDetails?.title || (isLoading ? "Loading..." : "Unknown Title")}
                </Typography>
              </Box>
            </Box>
            {(() => {
              // a weak connection is reported in the same slot as the artist line
              const compactStatus = displayError || statusText;
              if (compactStatus) {
                return (
                  <Box
                    onClick={(e) => {
                      if (!statusCanRetry) return;
                      e.stopPropagation();
                      onRetryAudio();
                    }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      maxWidth: { xs: 170, sm: 320 },
                      cursor: statusCanRetry ? "pointer" : "default",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: displayError
                          ? "#ff8a80"
                          : isBuffering
                          ? "#ffd166"
                          : "rgba(255,255,255,0.75)",
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {compactStatus}
                    </Typography>
                    {statusCanRetry && (
                      <Refresh sx={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }} />
                    )}
                  </Box>
                );
              }
              return audioDetails?.artist ? (
                <Box sx={{ overflow: "hidden", width: { xs: 140, sm: 300 }, position: "relative" }}>
                <Box
                  sx={{
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    animation: `${marquee} 10s linear infinite`,
                    "&:hover": { animationPlayState: "paused" },
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.6)",
                      display: "block",
                    }}
                  >
                    {audioDetails.artist} &nbsp;&nbsp; {audioDetails.artist}
                  </Typography>
                </Box>
              </Box>
              ) : null;
            })()}
          </Box>
        </Box>

        {/* Spacer fills middle */}
        <Box sx={{ flex: 1 }} />

        {/* Controls — right aligned */}
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
        >
          <IconButton
            onClick={onNavigateBack}
            disabled={isLoading}
            sx={{ color: "rgba(255,255,255,0.9)", fontSize: 24 }}
          >
            <SkipPrevious fontSize="small" />
          </IconButton>

          <IconButton
            onClick={handlePlayPause}
            disabled={isLoading}
            sx={{
              bgcolor: "rgba(255,255,255,0.15)",
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            {isLoading || isBuffering ? (
              <CircularProgress size={24} color="inherit" />
            ) : isPlaying ? (
              <Pause fontSize="medium" />
            ) : (
              <PlayArrow fontSize="medium" />
            )}
          </IconButton>

          <IconButton
            onClick={handleNext}
            disabled={isLoading || (!isPlaylistMode && !nextTracks.length)}
            sx={{ color: "rgba(255,255,255,0.9)", fontSize: 24 }}
          >
            <SkipNext fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  // Full mode
  return (
    <>
      {/* Progress slider */}
      <Box sx={{ width: "100%", maxWidth: { xs: 300, sm: 450 }, mb: 0 }}>
        <Slider
          value={scrubTime ?? currentTime}
          max={duration || 0}
          onChange={(_, v) => setScrubTime(v as number)}
          onChangeCommitted={(_, v) => {
            const t = v as number;
            onSeek(t);
            setScrubTime(null);
          }}
          disabled={isLoading || !!displayError}
          sx={{
            color: "rgba(255,255,255,0.8)",
            "& .MuiSlider-rail": {
              opacity: 1,
              bgcolor: "transparent",
              background: `linear-gradient(to right, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.4) ${duration ? (bufferedTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) ${duration ? (bufferedTime / duration) * 100 : 0}%, rgba(255,255,255,0.2) 100%)`,
            },
            "& .MuiSlider-thumb": {
              bgcolor: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "white", boxShadow: "0 0 10px rgba(255,255,255,0.5)" },
            },
          }}
        />
        <Box display="flex" justifyContent="space-between" sx={{ mb: 0 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
            {formatTime(scrubTime ?? currentTime)}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.75rem" }}>
            {formatTime(duration || 0)}
          </Typography>
        </Box>
      </Box>

      {/* Status line: error, or an honest "the network is behind" message */}
      <Box sx={{ width: "100%", maxWidth: { xs: 300, sm: 450 }, minHeight: 44, mb: 0.5 }}>
        {displayError ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                startIcon={<Refresh />}
                onClick={fetchError ? onFetchData : onRetryAudio}
              >
                Retry
              </Button>
            }
          >
            {displayError}
          </Alert>
        ) : statusText ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: "rgba(0,0,0,0.45)",
              border: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <CircularProgress size={13} sx={{ color: "#ffd166", flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ color: "rgba(255,255,255,0.9)", flex: 1, lineHeight: 1.3 }}
            >
              {statusText}
            </Typography>
            {statusCanRetry && (
              <Button
                size="small"
                startIcon={<Refresh />}
                onClick={onRetryAudio}
                sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.7rem", minWidth: 0 }}
              >
                Retry now
              </Button>
            )}
          </Box>
        ) : null}
      </Box>

      {/* Playback controls */}
      <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }} sx={{ mt: 0.5 }}>
        <IconButton
          onClick={onNavigateBack}
          disabled={isLoading}
          sx={{ color: "rgba(255,255,255,0.9)" }}
        >
          <SkipPrevious fontSize="small" />
        </IconButton>

        <IconButton
          onClick={handlePlayPause}
          size="medium"
          disabled={isLoading}
          sx={{
            bgcolor: "rgba(255,255,255,0.1)",
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          {isLoading || isBuffering ? (
            <CircularProgress size={32} color="inherit" />
          ) : isPlaying ? (
            <Pause fontSize="inherit" />
          ) : (
            <PlayArrow fontSize="inherit" />
          )}
        </IconButton>

        <IconButton
          onClick={handleNext}
          disabled={isLoading || (!isPlaylistMode && !nextTracks.length)}
          sx={{ color: "rgba(255,255,255,0.9)" }}
        >
          <SkipNext fontSize="small" />
        </IconButton>
      </Box>

      {/* Autoplay and playlist controls */}
      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Checkbox
          checked={autoPlay}
          onChange={(e) => {
            setAutoPlay(e.target.checked);
            localStorage.setItem("autoPlay", String(e.target.checked));
          }}
          icon={<PlayCircleOutline />}
          checkedIcon={<PlayArrow />}
          sx={{
            p: 0,
            m: 0,
            color: "rgba(255,255,255,0.6)",
            "&.Mui-checked": { color: "#1db954" },
            "&:hover": { backgroundColor: "transparent", color: "#1db954" },
            "& .MuiSvgIcon-root": {
              fontSize: { xs: 28, sm: 32 },
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
            },
          }}
        />

        {isPlaylistMode && (
          <>
            <Checkbox
              checked={loopPlaylist}
              onChange={(e) => setLoopPlaylist(e.target.checked)}
              icon={<LoopOutlined />}
              checkedIcon={<Loop />}
              sx={{
                p: 0,
                m: 0,
                color: "rgba(255,255,255,0.6)",
                "&.Mui-checked": { color: "#ff6b35" },
                "&:hover": { backgroundColor: "transparent", color: "#ff6b35" },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: 24, sm: 28 },
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
                },
              }}
            />
            <Checkbox
              checked={shufflePlaylist}
              onChange={(e) => setShufflePlaylist(e.target.checked)}
              icon={<ShuffleOutlined />}
              checkedIcon={<Shuffle />}
              sx={{
                p: 0,
                m: 0,
                color: "rgba(255,255,255,0.6)",
                "&.Mui-checked": { color: "#9c27b0" },
                "&:hover": { backgroundColor: "transparent", color: "#9c27b0" },
                "& .MuiSvgIcon-root": {
                  fontSize: { xs: 24, sm: 28 },
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.4))",
                },
              }}
            />
          </>
        )}
      </Box>

      {/* Next track */}
      {autoPlay && nextTracks.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            <PlayArrow fontSize="small" sx={{ color: "#1db954" }} />
            Next up:{" "}
            <Link
              component="button"
              onClick={() => navigate(`/music/${nextTracks[0].vid}`)}
              sx={{
                color: "rgba(255,255,255,0.95)",
                textDecoration: "none",
                fontWeight: "medium",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {nextTracks[0].title}
            </Link>
          </Typography>
        </Box>
      )}
    </>
  );
};

interface PlayerProps {
  playlist?: any[];
  startIndex?: number;
  onRemove?: (vid: string) => void;
  searchResults?: any[];
  playlistId?: string;
}

// --- Main Player Component
const MusicPlayer: React.FC<PlayerProps> = ({ playlist, startIndex = 0, onRemove, searchResults, playlistId }) => {
  const { vid: vidParam } = useParams<{ vid: string }>();
  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement>(null);

  const [audioDetails, setAudioDetails] = useState<AudioDetails | null>(null);
  const [autoPlay, setAutoPlay] = useState<boolean>(
    () => localStorage.getItem("autoPlay") === "true"
  );
  const [nextTracks, setNextTracks] = useState<RandomAudio[]>([]);
  const [playlistIndex, setPlaylistIndex] = useState<number>(startIndex);
  const [loopPlaylist, setLoopPlaylist] = useState<boolean>(false);
  const [shufflePlaylist, setShufflePlaylist] = useState<boolean>(false);

  const [artworkError, setArtworkError] = useState(false);
  const [artworkRetry, setArtworkRetry] = useState(0);

  const [listMode] = useState<'default' | 'random'>(() =>
    (localStorage.getItem("videoListMode") as 'default' | 'random' | null) || 'default'
  );

  const [fetchError, setFetchError] = useState<string | null>(null);
  const [playlistData, setPlaylistData] = useState<any[]>(playlist || []);

  // IntersectionObserver to detect when main controls scroll out of view
  const playerCardRef = useRef<HTMLDivElement>(null);
  const [showBottomBar, setShowBottomBar] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const isPlaylistMode = !!playlist && playlist.length > 0;
  
  // Use internal state for playlist to support optimistic updates
  useEffect(() => {
    setPlaylistData(playlist || []);
  }, [playlist]);
  
  const currentEntry = isPlaylistMode ? playlistData[playlistIndex] : null;
  const currentVid = isPlaylistMode ? currentEntry?.vid : vidParam;
  const thumbnailUrl = currentVid ? `${backendUrl}/api/thumbnail/${currentVid}` : null;
  const audioUrl = currentVid ? `${backendUrl}/api/a/${currentVid}` : null;

  // --- Background click handler
  const handleBackgroundClick = () => {
    if (!isPlaylistMode && currentVid) {
      navigate(`/video/${currentVid}`);
    }
  };

  // --- Audio player hook
  const handlePlaylistEnd = useCallback(() => {
    if (!playlistData || playlistData.length === 0) return;
    if (shufflePlaylist) {
      // pick a random index (allow same as current occasionally)
      const r = Math.floor(Math.random() * playlistData.length);
      setPlaylistIndex(r);
      return;
    }
    if (loopPlaylist) {
      setPlaylistIndex((i) => (i + 1) % playlistData.length);
      return;
    }
    // no loop: advance until end then stop
    setPlaylistIndex((i) => {
      if (i + 1 < playlistData.length) return i + 1;
      return i;
    });
  }, [playlistData, loopPlaylist, shufflePlaylist]);

  const onAudioEndCallback = isPlaylistMode ? handlePlaylistEnd : () => {
    if (autoPlay && nextTracks.length > 0) {
      const nextTrack = nextTracks[0];
      setNextTracks(nextTracks.slice(1));
      navigate(`/music/${nextTrack.vid}`);
    }
  };

  const {
    status: playbackStatus,
    isPlaying,
    duration,
    currentTime,
    bufferedTime,
    bufferedAhead,
    stallSeconds,
    attempt,
    isLoading: isAudioLoading,
    isBuffering,
    isOffline,
    error: audioPlayerError,
    play: playAudio,
    pause: pauseAudio,
    togglePlay: toggleAudioPlayback,
    seekTo,
    retry: retryAudioLoading,
  } = useAudioPlayer(audioRef, {
    src: audioUrl,
    // track changes in a playlist / autoplay must start on their own
    autoPlay: true,
    onEnded: onAudioEndCallback,
  });

  // Handle playlist reordering with optimistic updates
  const handleRemove = async (signal: string | undefined, reorderedPlaylist?: any[]) => {
    if (typeof signal === 'string' && signal.startsWith('REORDER_')) {
      // If reorderedPlaylist is provided (optimistic update), apply it immediately
      if (reorderedPlaylist && playlistId) {
        setPlaylistData([...reorderedPlaylist]);
        
        // Persist the order to backend in the background
        try {
          const videosPayload = reorderedPlaylist.map((video, index) => ({
            vid: video.vid,
            rank: index + 1,
          }));

          await axiosInstance.patch(`${backendUrl}/api/playlists/${playlistId}/reorder`, {
            videos: videosPayload,
          });
        } catch (e) {
          console.error('Failed to persist reordered playlist:', e);
          // Revert on error by fetching fresh data
          try {
            const resp = await axiosInstance.get(`${backendUrl}/api/playlists/${playlistId}`);
            setPlaylistData([...resp.data.videos]);
          } catch (fetchError) {
            console.error('Failed to revert playlist order:', fetchError);
          }
        }
      }
    } else if (signal && onRemove) {
      // Original remove behavior
      onRemove(signal);
    }
  };

  // Handle play from DraggableVideoList
  const handlePlay = (vid: string) => {
    const idx = playlistData.findIndex((v: any) => v.vid === vid);
    if (idx !== -1) {
      setPlaylistIndex(idx);
    }
  };

  // --- Fetch details
  const fetchData = useCallback(async () => {
    if (!currentVid) return;
    try {
      setFetchError(null);
      const res = await axiosInstance.get(`${backendUrl}/api/stats/${currentVid}`);
      setAudioDetails(res.data);
    } catch (err: any) {
      setFetchError(err.message || "Failed to load audio details.");
    }
  }, [currentVid, backendUrl]);

  useEffect(() => {
    fetchData();
    if (!isPlaylistMode && autoPlay) {
      if (listMode === 'random') {
        const seed = Math.random().toString(36).slice(2, 10);
        axiosInstance
          .get(`${backendUrl}/api/videos/random-seeded?seed=${seed}&page=1&limit=12`)
          .then((res) => setNextTracks(res.data.videos))
          .catch(console.error);
      } else {
        axiosInstance
          .get(`${backendUrl}/api/videos/random?currentVid=${vidParam}&limit=1`)
          .then((res) => setNextTracks(res.data.videos))
          .catch(console.error);
      }
    }
  }, [currentVid, isPlaylistMode, autoPlay, backendUrl, fetchData, vidParam, listMode]);

  useEffect(() => {
    document.title = audioDetails?.title ? `${audioDetails.title} - MediaPlayer` : 'MediaPlayer';
    return () => { document.title = 'MediaPlayer'; };
  }, [audioDetails?.title]);

  // --- Thumbnail retry
  useEffect(() => {
    if (artworkError && artworkRetry < 3) {
      const timer = setTimeout(() => {
        setArtworkRetry((r) => r + 1);
        setArtworkError(false);
      }, 1000 * Math.pow(2, artworkRetry));
      return () => clearTimeout(timer);
    }
  }, [artworkError, artworkRetry]);

  // --- IntersectionObserver for bottom bar visibility
  useEffect(() => {
    const el = playerCardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowBottomBar(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "-56px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // --- MediaSession setup
  useEffect(() => {
    if ("mediaSession" in navigator && audioDetails) {
      const artworkSrc =
        !artworkError && thumbnailUrl
          ? `${thumbnailUrl}?retry=${artworkRetry}`
          : ""; // fallback

      navigator.mediaSession.metadata = new MediaMetadata({
        title: audioDetails?.title || "Unknown Title",
        artist: audioDetails?.artist || "Unknown Artist",
        artwork: artworkSrc
          ? [{ src: artworkSrc, sizes: "300x300", type: "image/jpeg" }]
          : [],
      });

      navigator.mediaSession.setActionHandler("play", () => {
        playAudio();
      });
      navigator.mediaSession.setActionHandler("pause", () => {
        pauseAudio();
      });
      navigator.mediaSession.setActionHandler("nexttrack", () => {
        if (!isPlaylistMode) {
          if (nextTracks.length > 0) {
            navigate(`/music/${nextTracks[0].vid}`);
          }
        } else if (playlist && playlist.length > 0) {
          // advance according to playlist settings
          if (shufflePlaylist) {
            const r = Math.floor(Math.random() * playlist.length);
            setPlaylistIndex(r);
          } else if (loopPlaylist) {
            setPlaylistIndex((i) => (i + 1) % playlist.length);
          } else {
            setPlaylistIndex((i) => Math.min(i + 1, playlist.length - 1));
          }
        }
      });
      navigator.mediaSession.setActionHandler("previoustrack", () => {
        navigate(-1);
      });
    }
  }, [currentVid, audioDetails, navigate, nextTracks, thumbnailUrl, artworkError, artworkRetry, isPlaylistMode, playlist, shufflePlaylist, loopPlaylist, playAudio, pauseAudio]);

  const isLoading = isAudioLoading;
  const displayError = fetchError || audioPlayerError;

  // Honest connection feedback (never claim "playing" while nothing loads)
  const playbackHint = describePlayback({
    status: playbackStatus,
    isOffline,
    isBuffering,
    bufferedAhead,
    stallSeconds,
    attempt,
  });
  const statusText = fetchError ? null : playbackHint ? playbackHint.text : null;
  const statusCanRetry = !fetchError && !!playbackHint && playbackHint.escalate;

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Background with ambient lighting — YouTube-style blurred glow */}
      {thumbnailUrl && !artworkError ? (
        <>
          {/* Layer 1: Ambient glow — heavily blurred thumbnail for soft diffused lighting */}
          <Box
            component="img"
            src={`${thumbnailUrl}?retry=${artworkRetry}`}
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              objectFit: 'cover',
              objectPosition: 'center',
              zIndex: -3,
              pointerEvents: 'none',
              filter: 'blur(80px) saturate(200%) brightness(0.6)',
              transform: 'scale(1.3)',
              opacity: 0.55,
              transition: 'opacity 1.2s ease-out',
              animation: `${fadeIn} 1.5s ease-out`,
            }}
          />

          {/* Layer 2: Sharp thumbnail — visible artwork in center */}
          <Box
            component="img"
            src={`${thumbnailUrl}?retry=${artworkRetry}`}
            onError={() => setArtworkError(true)}
            onClick={handleBackgroundClick}
            alt="Background"
            sx={{
              position: 'fixed',
              top: 56,
              left: 0,
              width: '100vw',
              height: '100vh',
              objectFit: 'contain',
              objectPosition: 'top center',
              zIndex: -2,
              animation: `${fadeIn} 0.8s ease-out`,
            }}
          />

          {/* Layer 3: Gradient overlay — fade to dark at bottom for controls readability */}
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: (theme) =>
                `linear-gradient(to bottom, 
                  rgba(0,0,0,0.15) 0%, 
                  rgba(0,0,0,0.35) 70%, 
                  rgba(0,0,0,0.35) 80%, 
                  rgba(0,0,0,0.65) 90%, 
                  ${theme.palette.background.default} 100%)`,
              zIndex: -1,
              pointerEvents: 'none',
            }}
          />
        </>
      ) : null}

     <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Container maxWidth={isPlaylistMode ? false : "sm"} sx={{ pt: { xs: 2, sm: 4 }, mb: 2, px: { xs: 0, sm: 2 } }} ref={playerCardRef}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: { xs: 0, sm: 3 },
              bgcolor: 'rgba(0,0,0,0)',
              backdropFilter: 'none',
              border: 'none',
              boxShadow: 'none'
            }}
          >
          <div
            onClick={handleBackgroundClick}
            style={{
              width: '100%',
              height: '320px',
              border: 'none',
            }}
          />
            <Box display="flex" flexDirection="column" alignItems="center">
              {/* Track info with subtle transparency */}
              <Typography variant="subtitle1" gutterBottom sx={{ color: 'rgba(255,255,255,0.95)', textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
                {audioDetails?.title || (isLoading ? "Loading..." : "Unknown Title")}
              </Typography>

              <PlaybackControls
                audioDetails={audioDetails}
                currentTime={currentTime}
                duration={duration}
                bufferedTime={bufferedTime}
                isLoading={isLoading}
                isBuffering={isBuffering}
                isPlaying={isPlaying}
                displayError={displayError}
                autoPlay={autoPlay}
                loopPlaylist={loopPlaylist}
                shufflePlaylist={shufflePlaylist}
                nextTracks={nextTracks}
                isPlaylistMode={isPlaylistMode}
                playlist={playlist || []}
                playlistIndex={playlistIndex}
                onTogglePlay={toggleAudioPlayback}
                onSeek={seekTo}
                statusText={statusText}
                statusCanRetry={statusCanRetry}
                onFetchData={fetchData}
                onRetryAudio={retryAudioLoading}
                fetchError={fetchError}
                setAutoPlay={setAutoPlay}
                setLoopPlaylist={setLoopPlaylist}
                setShufflePlaylist={setShufflePlaylist}
                setPlaylistIndex={setPlaylistIndex}
                onNavigateBack={() => navigate(-1)}
                onNavigateNext={(v) => navigate(`/music/${v}`)}
                mode="full"
              />

              <audio ref={audioRef} playsInline preload="auto" />
            </Box>
          </Paper>
        </Container>

        {/* Video list component - positioned after player */}
        {isPlaylistMode && playlistData.length > 0 ? (
          <Container maxWidth={false} sx={{ mt: 1, mb: 2, px: 0, paddingLeft: "0 !important", paddingRight: "0 !important" }}>
            <DraggableVideoList
              playlist={playlistData.map((v: any, idx: number) => ({
                ...v,
                index: playlistIndex === idx ? 0 : undefined,
              }))}
              playlistId={playlistId || ''}
              currentVid={currentVid}
              onRemove={handleRemove}
              onPlay={handlePlay}
            />
          </Container>
        ) : (
          <VideoList searchResults={searchResults} />
        )}

        {/* Compact bottom playback bar */}
        <Box
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            bgcolor: alpha('#000', 0.85),
            backdropFilter: 'blur(20px) saturate(180%)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            transform: showBottomBar ? 'translateY(0)' : 'translateY(100%)',
            transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            overflow: 'hidden',
          }}
        >
          {/* Ambient glow progress bar */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: `${(currentTime / (duration || 1)) * 100}%`,
              bgcolor: 'rgba(255, 255, 255, 0.07)',
              boxShadow: 'inset 0 0 20px rgba(255, 255, 255, 0.1)',
              transition: 'width 0.3s linear',
              zIndex: 0,
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <PlaybackControls
              audioDetails={audioDetails}
              currentTime={currentTime}
              duration={duration}
              bufferedTime={bufferedTime}
              isLoading={isLoading}
              isBuffering={isBuffering}
              isPlaying={isPlaying}
              displayError={displayError}
              autoPlay={autoPlay}
              loopPlaylist={loopPlaylist}
              shufflePlaylist={shufflePlaylist}
              nextTracks={nextTracks}
              isPlaylistMode={isPlaylistMode}
              playlist={playlist || []}
              playlistIndex={playlistIndex}
              onTogglePlay={toggleAudioPlayback}
              onSeek={seekTo}
              statusText={statusText}
              statusCanRetry={statusCanRetry}
              onFetchData={fetchData}
              onRetryAudio={retryAudioLoading}
              fetchError={fetchError}
              setAutoPlay={setAutoPlay}
              setLoopPlaylist={setLoopPlaylist}
              setShufflePlaylist={setShufflePlaylist}
              setPlaylistIndex={setPlaylistIndex}
              onNavigateBack={() => navigate(-1)}
              onNavigateNext={(v) => navigate(`/music/${v}`)}
              mode="compact"
            />
          </Box>
        </Box>
       </Box>
      </Box>
    );
  };

export default MusicPlayer;